import { DragDropModule } from '@angular/cdk/drag-drop';
import { ObserversModule } from '@angular/cdk/observers';
import { CdkTreeModule } from '@angular/cdk/tree';
import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatMenuModule } from '@angular/material/menu';
import { RouterModule } from '@angular/router';
import { SvgModule } from '@campus/svg';
import { UtilsModule } from '@campus/utils';
import { dcrIconService } from '@diekeure/dcr-components';
import { AdaptiveLevelCheckboxComponent } from './adaptive-level-checkbox/adaptive-level-checkbox.component';
import { AdaptiveModuleConceptsDirective } from './adaptive-module/adaptive-module-concepts.directive';
import { AdaptiveModuleComponent } from './adaptive-module/adaptive-module.component';
import {
  AdaptiveTocCardComponent,
  AdaptiveTocCardContentDirective,
  AdaptiveTocCardHintDirective,
} from './adaptive-toc-card';
import { AppShellComponent } from './app-shell';
import { BadgeComponent } from './badge/badge.component';
import { KaiRecommendBubbleComponent } from './bubble/kai-recommend/kai-recommend-bubble.component';
import {
  ElevatedButtonComponent,
  FilledButtonComponent,
  InlineButtonComponent,
  InlineIconButtonComponent,
  OutlineButtonComponent,
  OutlineIconButtonComponent,
  SegmentedDropdownButtonComponent,
  TonalButtonComponent,
  ToolbarButtonComponent,
} from './button';
import { ToolbarButtonGroupComponent } from './button-groups';
import { ButtonIconDirective, ButtonSuffixIconDirective } from './button/button-core/button-icon.directive';
import { ConceptButtonIconDirective } from './button/concept-button/concept-button-icon.directive';
import { ConceptButtonKaiDirective } from './button/concept-button/concept-button-kai.directive';
import { ConceptButtonLabelDirective } from './button/concept-button/concept-button-label.directive';
import { ConceptButtonComponent } from './button/concept-button/concept-button.component';
import { FabButtonComponent } from './button/fab-button/fab-button.component';
import { OutlinedPlayerButtonComponent } from './button/outlined-player-button/outlined-player-button.component';
import { TimerButtonComponent } from './button/timer-button/timer-button.component';
import { ToggleIconButtonComponent } from './button/toggle-icon-button/toggle-icon-button.component';
import { CameraComponent } from './camera/camera.component';
import { CappedChipComponent } from './capped-chip/capped-chip.component';
import {
  CardActionAreaDirective,
  CardAvatarHeadDirective,
  CardHeaderActionAreaDirective,
  CardHeadlineDirective,
  CardHeroDirective,
  CardHintDirective,
  CardMediaDirective,
  CardProgressComponent,
  CardSubheadDirective,
  CardTextDirective,
  ElevatedCardComponent,
  FilledCardComponent,
  OutlineCardComponent,
} from './card';
import { CardCoreComponent } from './card/card-core/card-core.component';
import { CardOverlineheadDirective } from './card/card-core/card-overline-head.directive';
import { CheckboxListComponent } from './checkbox-list/checkbox-list.component';
import { CheckboxComponent } from './checkbox/checkbox.component';
import {
  ChipAvatarDirective,
  ChipGroupComponent,
  ChipsInputComponent,
  FilterChipComponent,
  InputChipComponent,
  SuggestionChipComponent,
} from './chips';
import { CollapsibleSheetComponent } from './collapsible-sheet';
import { RewardDirective, SpeechBubbleComponent } from './feedback';
import { FeedbackHostDirective } from './feedback/feedback-host.directive';
import { ClaimRewardComponent } from './feedback/reward/claim-reward.component';
import { FocusRingComponent } from './focus-ring/focus-ring.component';
import { BooleanValueAccessor } from './forms/accessors/boolean-value.accessor';
import { DateValueAccessor } from './forms/accessors/date-value.accessor';
import { MultiNumberValueAccessor } from './forms/accessors/multi-number-value.accessor';
import { MultiSelectValueAccessor } from './forms/accessors/multi-select-value.accessor';
import { NumberValueAccessor } from './forms/accessors/number-value.accessor';
import { RadioButtonValueAccessor } from './forms/accessors/radio-button-value.accessor';
import { RadioGroupValueAccessor } from './forms/accessors/radio-group-value.accessor';
import { SelectValueAccessor } from './forms/accessors/select-value.accessor';
import { SelectedValueAccessor } from './forms/accessors/selected-value.accessor';
import { TextValueAccessor } from './forms/accessors/text-value.accessor';
import { DcrCheckboxComponent } from './forms/proxies/dcr-checkbox';
import { DcrFilterChipComponent } from './forms/proxies/dcr-filter-chip';
import { DcrRadioButtonComponent } from './forms/proxies/dcr-radio-button';
import { DcrRadioGroupComponent } from './forms/proxies/dcr-radio-group';
import { DcrSelectComponent, DcrSelectOptionComponent } from './forms/proxies/dcr-select';
import { DcrSwitchComponent } from './forms/proxies/dcr-switch';
import { DcrTextFieldComponent } from './forms/proxies/dcr-text-field';
import { FroalaViewerComponent } from './froala-viewer';
import { dcrIcons, icons } from './icons';
import { LevelIndicatorComponent } from './level-indicator/level-indicator.component';
import { LevelComponent } from './levels/level.component';
import { LoadingComponent } from './loading/loading.component';
import { MediaPlayerComponent } from './media-player/media-player.component';
import { MenuItemComponent } from './menu/menu-item/menu-item.component';
import { MenuComponent } from './menu/menu.component';
import { NavigationDrawerComponent, NavigationDrawerItemComponent } from './navigation';
import { NavigationRailItemComponent } from './navigation/navigation-rail/navigation-rail-item.component';
import { NavigationRailComponent } from './navigation/navigation-rail/navigation-rail.component';
import { NavigationTreeItemComponent } from './navigation/navigation-tree/navigation-tree-item.component';
import { NavigationTreeComponent } from './navigation/navigation-tree/navigation-tree.component';
import { PercentageMeterComponent } from './percentage-meter';
import { ProductListComponent } from './product-list/product-list.component';
import { ProductComponent } from './product-list/product.component';
import {
  AbilityArcComponent,
  AbilityProgressComponent,
  LinearProgressBarComponent,
  SegmentedProgressBarComponent,
} from './progress';
import { StatusProgressComponent } from './progress/status-progress/status-progress.component';
import { OptionGroupComponent } from './select/option-group/option-group.component';
import { SelectOptionComponent } from './select/select-option/select-option.component';
import { SelectComponent } from './select/select.component';
import { SelectorWheelComponent } from './selector-wheel/selector-wheel.component';
import { StateLayerComponent } from './state/state-layer.component';
import { SwitchComponent } from './switch/switch.component';
import { Table2LocalHandlingDirective } from './table/local-handling';
import { TableRowComponent } from './table/row/row.component';
import { TableComponent } from './table/table.component';
import { TextFieldComponent } from './text-field/text-field.component';
import { TocHeaderButtonComponent } from './toc-header/toc-header-button.component';
import { TocHeaderHeadlineDirective } from './toc-header/toc-header-headline.directive';
import { TocHeaderImageComponent } from './toc-header/toc-header-image.component';
import { TocHeaderOverlineDirective } from './toc-header/toc-header-overline.directive';
import { TocHeaderSelectComponent } from './toc-header/toc-header-select.component';
import { TocHeaderSubheadDirective } from './toc-header/toc-header-subhead.directive';
import { TocHeaderComponent } from './toc-header/toc-header.component';
import { PlainTooltipComponent } from './tooltip/plain-tooltip.component';
import { RichTooltipComponent } from './tooltip/rich-tooltip.components';
import { TooltipService } from './tooltip/tooltip.service';
import { TouchTargetComponent } from './touch/touch-target.component';
import { TreeComponent } from './tree/tree.component';
import { VerticalSplitBottomDirective } from './vertical-split/vertical-split-top.bottom';
import { VerticalSplitTopDirective } from './vertical-split/vertical-split-top.directive';
import { VerticalSplitComponent } from './vertical-split/vertical-split.component';
import { VideoComponent } from './video/video.component';

dcrIconService.registerIcons(dcrIcons);

@NgModule({
  providers: [TooltipService],
  imports: [
    CommonModule,
    UtilsModule,
    FormsModule,
    ReactiveFormsModule,
    ObserversModule,
    SvgModule,
    MatMenuModule,
    SvgModule.forRoot({
      iconMapping: icons,
    }),
    RouterModule,
    CdkTreeModule,
    DragDropModule,
    MatAutocompleteModule,
    DcrRadioGroupComponent,
    RadioGroupValueAccessor,
    MultiNumberValueAccessor,
    DcrRadioButtonComponent,
    RadioButtonValueAccessor,
    DcrSwitchComponent,
    DcrFilterChipComponent,
  ],
  declarations: [
    FroalaViewerComponent,
    AdaptiveTocCardComponent,
    AdaptiveTocCardContentDirective,
    AdaptiveTocCardHintDirective,
    ElevatedCardComponent,
    FilledCardComponent,
    OutlineCardComponent,
    CardCoreComponent,
    CardHeadlineDirective,
    CardSubheadDirective,
    CardHeroDirective,
    CardTextDirective,
    CardMediaDirective,
    CardActionAreaDirective,
    CardHintDirective,
    CardHeaderActionAreaDirective,
    CardProgressComponent,
    CardAvatarHeadDirective,
    InlineButtonComponent,
    OutlineButtonComponent,
    ToggleIconButtonComponent,
    ElevatedButtonComponent,
    FilledButtonComponent,
    TonalButtonComponent,
    TimerButtonComponent,
    InlineIconButtonComponent,
    OutlineIconButtonComponent,
    ToolbarButtonComponent,
    ToolbarButtonGroupComponent,
    SegmentedDropdownButtonComponent,
    FabButtonComponent,
    ButtonSuffixIconDirective,
    SegmentedProgressBarComponent,
    CardOverlineheadDirective,
    SelectorWheelComponent,
    LinearProgressBarComponent,
    AbilityProgressComponent,
    AbilityArcComponent,
    ConceptButtonComponent,
    ConceptButtonLabelDirective,
    ConceptButtonKaiDirective,
    ConceptButtonIconDirective,
    TocHeaderComponent,
    TocHeaderHeadlineDirective,
    TocHeaderSubheadDirective,
    TocHeaderImageComponent,
    TocHeaderSelectComponent,
    TocHeaderButtonComponent,
    TocHeaderOverlineDirective,
    OutlinedPlayerButtonComponent,
    AdaptiveModuleComponent,
    AdaptiveModuleConceptsDirective,
    SpeechBubbleComponent,
    StatusProgressComponent,
    ClaimRewardComponent,
    RewardDirective,
    FeedbackHostDirective,
    PercentageMeterComponent,
    ButtonIconDirective,
    ProductListComponent,
    ProductComponent,
    LevelComponent,
    LevelIndicatorComponent,
    AdaptiveLevelCheckboxComponent,
    CollapsibleSheetComponent,
    SuggestionChipComponent,
    InputChipComponent,
    FilterChipComponent,
    ChipGroupComponent,
    ChipAvatarDirective,
    ChipsInputComponent,
    BadgeComponent,
    VideoComponent,
    CameraComponent,
    SwitchComponent,
    StateLayerComponent,
    TouchTargetComponent,
    TextFieldComponent,
    NavigationRailComponent,
    NavigationRailItemComponent,
    NavigationDrawerComponent,
    NavigationDrawerItemComponent,
    SelectComponent,
    SelectOptionComponent,
    OptionGroupComponent,
    MenuComponent,
    MenuItemComponent,
    FocusRingComponent,
    TableComponent,
    TableRowComponent,
    Table2LocalHandlingDirective,
    CheckboxComponent,
    PlainTooltipComponent,
    RichTooltipComponent,
    CheckboxListComponent,
    MediaPlayerComponent,
    NavigationTreeComponent,
    NavigationTreeItemComponent,
    VerticalSplitComponent,
    VerticalSplitTopDirective,
    VerticalSplitBottomDirective,
    LoadingComponent,
    AppShellComponent,
    KaiRecommendBubbleComponent,
    TreeComponent,
    CappedChipComponent,

    TextValueAccessor,
    DcrTextFieldComponent,
    DcrSelectComponent,
    DcrSelectOptionComponent,
    SelectValueAccessor,
    MultiSelectValueAccessor,
    NumberValueAccessor,
    DateValueAccessor,
    BooleanValueAccessor,
    DcrCheckboxComponent,
    SelectedValueAccessor,
  ],
  exports: [
    FroalaViewerComponent,
    ButtonIconDirective,
    ButtonSuffixIconDirective,
    AdaptiveTocCardContentDirective,
    FilledCardComponent,
    CardHeadlineDirective,
    CardSubheadDirective,
    CardHeroDirective,
    CardTextDirective,
    CardMediaDirective,
    CardActionAreaDirective,
    CardHintDirective,
    CardHeaderActionAreaDirective,
    CardProgressComponent,
    CardAvatarHeadDirective,
    InlineButtonComponent,
    OutlineButtonComponent,
    ToggleIconButtonComponent,
    ElevatedButtonComponent,
    FilledButtonComponent,
    TonalButtonComponent,
    TimerButtonComponent,
    InlineIconButtonComponent,
    OutlineIconButtonComponent,
    ToolbarButtonComponent,
    ToolbarButtonGroupComponent,
    FabButtonComponent,
    SegmentedDropdownButtonComponent,
    SegmentedProgressBarComponent,
    CardOverlineheadDirective,
    SelectorWheelComponent,
    LinearProgressBarComponent,
    AbilityProgressComponent,
    ConceptButtonComponent,
    ConceptButtonLabelDirective,
    ConceptButtonIconDirective,
    ConceptButtonKaiDirective,
    TocHeaderComponent,
    TocHeaderHeadlineDirective,
    TocHeaderSubheadDirective,
    TocHeaderImageComponent,
    TocHeaderSelectComponent,
    TocHeaderOverlineDirective,
    TocHeaderButtonComponent,
    OutlinedPlayerButtonComponent,
    AdaptiveModuleComponent,
    AdaptiveModuleConceptsDirective,
    SpeechBubbleComponent,
    StatusProgressComponent,
    ClaimRewardComponent,
    RewardDirective,
    FeedbackHostDirective,
    PercentageMeterComponent,
    ElevatedCardComponent,
    AdaptiveTocCardComponent,
    AdaptiveTocCardHintDirective,
    OutlineCardComponent,
    ProductListComponent,
    ProductComponent,
    LevelComponent,
    LevelIndicatorComponent,
    AdaptiveLevelCheckboxComponent,
    CollapsibleSheetComponent,
    SuggestionChipComponent,
    InputChipComponent,
    FilterChipComponent,
    ChipGroupComponent,
    ChipAvatarDirective,
    ChipsInputComponent,
    BadgeComponent,
    VideoComponent,
    CameraComponent,
    SwitchComponent,
    StateLayerComponent,
    TouchTargetComponent,
    TextFieldComponent,
    NavigationRailComponent,
    NavigationRailItemComponent,
    NavigationDrawerComponent,
    NavigationDrawerItemComponent,
    NavigationTreeComponent,
    NavigationTreeItemComponent,
    SelectComponent,
    SelectOptionComponent,
    OptionGroupComponent,
    MenuComponent,
    MenuItemComponent,
    FocusRingComponent,
    TableComponent,
    TableRowComponent,
    Table2LocalHandlingDirective,
    CheckboxComponent,
    PlainTooltipComponent,
    RichTooltipComponent,
    CheckboxListComponent,
    MediaPlayerComponent,
    VerticalSplitComponent,
    VerticalSplitTopDirective,
    VerticalSplitBottomDirective,
    LoadingComponent,
    AppShellComponent,
    KaiRecommendBubbleComponent,
    TreeComponent,
    CappedChipComponent,

    DcrTextFieldComponent,
    TextValueAccessor,
    DcrRadioGroupComponent,
    DcrRadioButtonComponent,
    RadioButtonValueAccessor,
    RadioGroupValueAccessor,
    DcrSelectComponent,
    DcrSelectOptionComponent,
    DcrFilterChipComponent,
    SelectValueAccessor,
    MultiSelectValueAccessor,
    NumberValueAccessor,
    MultiNumberValueAccessor,
    DateValueAccessor,
    DcrSwitchComponent,
    DcrCheckboxComponent,
    BooleanValueAccessor,
    SelectedValueAccessor,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ComponentsModule {}
